<template>
  <div class="controlled-tour-map">
    <div class="actions flex justify-between mb-10">
      <div class="flex gap-x-5">
        <Switch v-model="showStartpoints" :label="$t('pages.dashboard.overview.showStartpoints')" />
        <Switch v-model="showFullTours" :label="$t('pages.dashboard.overview.showFullTours')" />
        <Switch v-model="showEndpoints" :label="$t('pages.dashboard.overview.showEndpoints')" />
      </div>
      <div class="flex gap-x-5">
        <div class="flex flex-col">
          <FormInput
            v-model="displayedLinesThreshold"
            :label="$t('pages.dashboard.overview.displayedLinesThreshold')"
            :min="0"
            class="w-full"
            type="number"
          />
        </div>
        <div class="flex flex-col">
          <div v-if="hasEmbeddedDateRange" class="flex items-end">
            <DateRange
              v-model="dateRange"
              :label="$t('campaign.dateRangeRestricted')"
              mode="datetime"
            />
            <ClearIcon v-if="dateRangeRestriction === false" class="w-6 h-6 mb-2 ml-2 cursor-pointer" @click="clearDateRange()" />
          </div>
          <div v-if="dateRangeRestriction !== false" class="text-pane-label mt-2">
            {{ $t('campaign.dateRangeRestrictedText') }}
          </div>
        </div>
        <custom-button v-if="lazy" variant="primary" @click="refetchPositions()">
          {{ $t('pages.dashboard.overview.reloadMap') }}
        </custom-button>
      </div>
    </div>
    <TourMap
      ref="tourMap"
      :positions="positions ?? []"
      :busy="loadingPositions"
      :show-endpoints="showEndpoints"
      :show-startpoints="showStartpoints"
      :show-tour="showFullTours"
      :auto-center="autoCenter"
      :show-last-known-postition="showLastKnownPosition"
      :initial-zoom="zoom ?? initialZoom"
      :displayed-lines-threshold="displayedLinesThresholdKm"
      @bounds="updateBounds"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { isEqual } from 'lodash';
import FormInput from '@utils/inputs/Input.vue';
import { useI18n } from 'vue-i18n';
import Switch from '@/components/utils/inputs/Switch.vue';
import DateRange from '@/components/utils/inputs/DateRange.vue';
import ClearIcon from '@/assets/images/remove_filter.svg?component';
import type { Company, Position } from '@/entities/Entities';
import { type DateRangeFilter, type Filter, useProvider } from '../table/hooks/provider';
import TourMap from './TourMap.vue';
import { defaultCenter } from '@/utils/map';
import { useNotify } from '../notifications';

interface Props {
  filter: Filter;
  company: Company;
  lazy?: boolean;
  hasEmbeddedDateRange?: boolean;
  dateRangeRestriction?: boolean | { start: Date; end: Date };
  autoCenter?: boolean;
  disabled?: boolean;
  useBounds?: boolean;
  center?: google.maps.LatLngLiteral;
  showLastKnownPosition?: boolean;
  initialZoom?: number;
}

const props = withDefaults(defineProps<Props>(), {
  lazy: true,
  center: () => defaultCenter,
  useBounds: true,
  dateRangeRestriction: false,
});

const notify = useNotify();

const { t } = useI18n();

const tourMap = ref();

const showStartpoints = ref(false);
const showEndpoints = ref(false);
const showFullTours = ref(true);
const bounds = ref();

const zoom = ref();

const dateRange = ref({
  start: props.dateRangeRestriction?.start ?? undefined,
  end: props.dateRangeRestriction?.end ?? undefined,
});

const displayedLinesThreshold = ref(100);
const displayedLinesThresholdKm = ref(0.1);

watch(displayedLinesThreshold, (newValue) => {
  if (!Number.isNaN(newValue) && newValue > 0) {
    // only update the km value if the input is a valid number
    displayedLinesThresholdKm.value = newValue / 1000;
  }
});

const clearDateRange = () => {
  dateRange.value = {
    start: undefined,
    end: undefined,
  };
};

const updateBounds = (newBounds: google.maps.LatLngLiteral) => {
  bounds.value = newBounds;
};

const getPositionFilters = computed(() => ({
  filter: {
    ...props.filter,
    ...(props.hasEmbeddedDateRange ? (dateRange.value?.start && dateRange.value?.end ? {
      tour_createdAt: { end: dateRange.value?.end, start: dateRange.value?.start } as DateRangeFilter,
    } : {}) : {
      tour_createdAt: props.filter.date ?? props.filter.createdAt as DateRangeFilter,
    }),
    tour_driver: props.filter?.driver,
    tour_license: props.filter?.license?.toString()?.split('/')?.at(-1),
    ...(props.filter?.['exists[license]'] ? { tour_license_exists: props.filter?.['exists[license]'] } : {}),
    ...(props.company ? { tour_license_company: props.company['@id']?.split('/')?.at(-1) } : {}),
    showFullTours: showFullTours.value,
    ...(bounds.value && props.useBounds
      ? {
        ne: `${bounds.value.north}|${bounds.value.east}`,
        sw: `${bounds.value.south}|${bounds.value.west}`,
      }
      : {}),
  },
}));

const {
  data: positions,
  loading: loadingPositions,
  refetch,
  error,
} = useProvider<Position[]>('/tours/positions', getPositionFilters.value, {
  immediate: !props.lazy && !props.disabled,
});

const refetchPositions = () => {
  refetch(getPositionFilters.value);
};

const moveToCenter = (lat: number, long: number) => {
  tourMap.value?.moveToCenter(lat, long);
};

watch(error, () => {
  if (error.value && !loadingPositions.value) {
    notify?.({
      variant: 'error',
      title: t('tourMap.error'),
    });
  }
});

defineExpose({
  moveToCenter,
});

watch(
  () => getPositionFilters.value.filter,
  async (filters, oldFilters) => {
    if (props.lazy || props.disabled) return;
    if (isEqual(filters, oldFilters)) return;
    if (!(dateRange.value?.start && dateRange.value?.end)) return;
    refetchPositions();
  },
  { deep: true },
);

watch(loadingPositions, (newValue) => {
  if (newValue) tourMap.value?.clearMap();
});
</script>

<style lang="scss" scoped>
.region {
  width: 20rem;
}
</style>
